/* eslint-disable camelcase */
import {
  Button,
  DialogContent,
  Typography,
  DialogTitle,
  Stepper,
  Step,
  StepLabel,
  Grid,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import createVehicleDescription from '../utils/createVehicleDescription';
import useGetVehicle from '../hooks/useGetVehicle';
import CustomerForm from './CustomerForm';
import CustomButton from './CustomButton';
import ClipForm from './ClipForm';
import { attemptClipPayment, fetchClipPayment, steps, updateClipPayment } from '../utils/clip';
import { postShopsLead } from '../utils/shopsLeads';
import { TopDialog } from './SendMessageModal';
import formatNumber from '../utils/formatNumber';

const useStyles = makeStyles(({ palette }) => ({
  circle: {
    backgroundColor: ({ color }) => palette[color].main,
    color: ({ color }) => palette[color].contrastText,
    '&:hover': {
      backgroundColor: ({ color }) => palette[color].main,
    },
    borderRadius: 100,
    fontSize: 12,
    padding: '10px 8px',
  },
  label: {
    fontSize: 14,
  },
}));

const InitialMessage = ({ handleCancel, handleContinue }) => (
  <div className="mb-3">
    <Typography variant="h5" className="font-bold text-center mb-5">
      ¡Empecemos con tu proceso de compra!
    </Typography>
    <Typography variant="p" className="font-bold">
      Nuestro sistema de apartado tiene 3 pasos:
    </Typography>
    <div className="d-flex mb-3">
      <span className="ms-2">1. Ingresar tu información de contacto.</span>
    </div>
    <div className="d-flex mb-3">
      <span className="ms-2">
        2. Apartar tu vehículo con un pago con tarjeta de crédito o débito.
      </span>
    </div>
    <div className="d-flex mb-3">
      <span className="ms-2">
        3. Acudir al Distribuidor Autorizado para continuar con tu proceso de compra.
      </span>
    </div>
    <div className="d-flex text-center">
      <CustomButton color="primary" className="my-5 px-16 py-3" onClick={handleContinue}>
        Continuar
      </CustomButton>
    </div>
    <div className="f-flex text-center">
      <Button onClick={handleCancel}>Cancelar</Button>
    </div>
  </div>
);

InitialMessage.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  handleContinue: PropTypes.func.isRequired,
};

const ErrorMessage = ({ vehicle, handleCancel }) => (
  <div className="mb-3">
    <Typography variant="h5" className="font-bold text-center mb-5">
      Lo sentimos.
    </Typography>
    <div className="d-block">
      <Typography variant="p" className="font-bold d-block">
        Hubo un error al procesar tu pago
      </Typography>
    </div>
    <Typography variant="p">
      Preséntate personalmente en nuestras oficinas para completar tu proceso de compra.
    </Typography>
    <Typography variant="h6" className="font-bold d-block mt-4">
      {vehicle.seller.name}
    </Typography>
    <Typography variant="p" className="d-block">
      {vehicle.seller.address}
    </Typography>
    <div className="d-flex text-center">
      <CustomButton color="primary" className="my-5 px-16 py-3" onClick={handleCancel}>
        De acuerdo
      </CustomButton>
    </div>
  </div>
);

ErrorMessage.propTypes = {
  vehicle: PropTypes.instanceOf(Object).isRequired,
  handleCancel: PropTypes.func.isRequired,
};

const SuccessMessage = ({ shopsPayment, clipResponse, vehicle, handleCancel }) => (
  <div className="mb-3">
    <Typography variant="h5" className="font-bold text-center mb-5">
      ¡Felicidades!
    </Typography>
    <div className="text-center mb-5">
      <Typography variant="p" className="font-bold d-block">
        Has separado tu vehículo
      </Typography>
      <Typography variant="h6" className="font-bold d-block mt-4">
        Orden #{clipResponse?.receipt_no}
      </Typography>
      <Typography variant="h6" className="font-bold d-block mt-4">
        ${shopsPayment?.amount} MXN
      </Typography>
      <Typography variant="p">
        Preséntate personalmente en nuestras oficinas para completar tu proceso de compra.
      </Typography>
      <Typography variant="h6" className="font-bold d-block mt-4">
        {vehicle.seller.name}
      </Typography>
      <Typography variant="p" className="d-block">
        {vehicle.seller.address}
      </Typography>
    </div>
    <CustomButton fullWidth color="primary" onClick={handleCancel} className="my-5 px-16 py-3">
      Completar
    </CustomButton>
  </div>
);

SuccessMessage.propTypes = {
  shopsPayment: PropTypes.instanceOf(Object),
  clipResponse: PropTypes.instanceOf(Object),
  vehicle: PropTypes.instanceOf(Object).isRequired,
  handleCancel: PropTypes.func.isRequired,
};

SuccessMessage.defaultProps = {
  shopsPayment: null,
  clipResponse: null,
};

const SeparateDialog = ({ handleCancel }) => {
  const [shopsPayment, setShopsPayment] = useState(null);
  const [clipResponse, setClipResponse] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (ticketId !== null && disabled) {
      setDisabled(false);
      setStep(step + 1);
    }
  }, [ticketId, disabled, step]);

  const { vehicle } = useGetVehicle();

  const handleCustomerCallback = (values) => {
    setCustomer(values);
    const data = {
      customer: values,
      ad: { title: createVehicleDescription(vehicle), url: window.location.href },
      interest: {
        unitId: vehicle.id,
      },
    };
    setDisabled(true);
    postShopsLead(data)
      .then((res) => {
        setTicketId(res.data.prospectId);
        setDisabled(false);
      })
      .catch(() => {
        setDisabled(false);
      });
  };

  const handleListener = (url, paymentId) => {
    window.addEventListener('message', (event) => {
      if (event.origin !== new URL(url).origin) {
        return; // Ignorar mensajes de otros orígenes
      }
      if (event.data?.paymentId) {
        const returnedPaymentId = event.data.paymentId;
        if (returnedPaymentId === paymentId) {
          fetchClipPayment(vehicle.seller.clipApiKey, paymentId)
            .then((data) => {
              updateClipPayment({ paymentId: data.id, status: data.status });
              const backdrop = document.querySelector('.MuiBackdrop-root');
              if (backdrop && backdrop !== null) backdrop.style.zIndex = -1;
              if (data.status === 'approved') return setStep('success');
              return setStep('error');
            })
            .catch((error) => {
              // updateClipPayment({ paymentId: data.id, status: data.status });
              const backdrop = document.querySelector('.MuiBackdrop-root');
              if (backdrop && backdrop !== null) backdrop.style.zIndex = -1;
              console.log(error);
              setStep('error');
            });
        }
      }
    });
  };

  const handle3DS = (url, paymentId) => {
    const iframeContainer = document.getElementById('3ds-iframe-container');
    iframeContainer.innerHTML = `<iframe title="cybersource3Ds" src="${url}" data-testid="cybersource3Ds-iframe" style="width: 100vw;height: 100vh;max-height: 600px;border:none;position:fixed;top:0;left:0;z-index:1000;"></iframe>`;
    iframeContainer.style.display = 'block';
    const backdrop = document.querySelector('.MuiBackdrop-root');
    if (backdrop && backdrop !== null) backdrop.style.zIndex = 1;
    handleListener(url, paymentId);
  };

  const handleSubmitPayment = (token, preventionData, address) => {
    const data = {
      preventionData,
      billingAddress: address,
      unitId: vehicle.id,
      ticketId,
      address,
      token,
    };
    setProcessing(true);
    attemptClipPayment(data)
      .then((clipData) => {
        const { response, payment, error } = clipData;
        setClipResponse(response);
        setShopsPayment(payment);
        setProcessing(false);
        if ((error && error !== null) || response.status === 'rejected') {
          return setStep('error');
        }
        if (response.status === 'pending' && response.pending_action?.url) {
          const pendingActionUrl = response.pending_action.url;
          return handle3DS(pendingActionUrl, response.id);
        }
        return setStep('success');
      })
      .catch((error) => {
        const backdrop = document.querySelector('.MuiBackdrop-root');
        if (backdrop && backdrop !== null) backdrop.style.zIndex = 1;
        setProcessing(false);
        console.log(error);
        setStep('error');
      });
  };

  const renderTitle = () => {
    if (step > 1) {
      return (
        <>
          <Typography variant={window.innerWidth < 767 ? 'p' : 'h5'} className="font-bold">
            PROCESO DE APARTADO
          </Typography>
          <Typography>
            Depósito de apartado
            {vehicle.seller.separateAmount
              ? `$${formatNumber(vehicle.seller.separateAmount)}`
              : '-'}
          </Typography>
        </>
      );
    }
    return (
      <Typography variant={window.innerWidth < 767 ? 'h6' : 'h4'} className="text-center">
        {vehicle.seller.name}
      </Typography>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 'error':
        return <ErrorMessage vehicle={vehicle} handleCancel={handleCancel} />;
      case 'success':
        return (
          <SuccessMessage
            shopsPayment={shopsPayment}
            clipResponse={clipResponse}
            handleCancel={handleCancel}
            vehicle={vehicle}
          />
        );
      case 2:
        return (
          <CustomerForm
            disabled={disabled}
            initialValues={customer}
            handleCancel={() => setStep(step - 1)}
            handleCustomerCallback={handleCustomerCallback}
          />
        );
      case 3:
        return (
          <ClipForm
            processing={processing}
            clipApiKey={vehicle.seller.clipApiKey}
            handleCancel={() => setStep(step - 1)}
            separateAmount={vehicle.seller.separateAmount}
            handleCardTokenCallback={handleSubmitPayment}
          />
        );
      default:
        return (
          <InitialMessage handleCancel={handleCancel} handleContinue={() => setStep(step + 1)} />
        );
    }
  };

  const styles = useStyles({ color: 'primary' });

  return (
    <TopDialog fullWidth open maxWidth="sm">
      <DialogTitle>
        {renderTitle()}
        <hr className="mt-4 border-primary border-b-2" />
        {window.innerWidth > 767 ? (
          <Stepper
            className="px-0 pb-0"
            activeStep={step - 1}
            orientation={window.innerWidth < 767 ? 'vertical' : 'horizontal'}
          >
            {steps.map(({ label }) => (
              <Step>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          <Grid container className="mt-3" alignItems="center">
            <Grid item xs={2}>
              <Typography variant="p" className={styles.circle}>
                {['success', 'error'].includes(step) ? 4 : step}/{steps.length}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <p className={styles.label}>
                {steps.find((current, index) => index === step - 1)?.label}
              </p>
            </Grid>
          </Grid>
        )}
      </DialogTitle>
      <DialogContent>{renderStep()}</DialogContent>
    </TopDialog>
  );
};

SeparateDialog.propTypes = {
  handleCancel: PropTypes.func.isRequired,
};

export default SeparateDialog;
